import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Leaf,
  MapPin,
  Phone,
  Mail,
  Linkedin,
} from "lucide-react";

const LINKEDIN_URL = "https://www.linkedin.com/company/saleem-enterprises-pakistan/";

const NewFooter: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [agreed, setAgreed] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email && agreed) {
      console.log("Newsletter subscription:", email);
      setEmail("");
      setAgreed(false);
    }
  };

  return (
    <footer className="bg-[#5A6B4D] text-white">

      {/* RESPONSIVE GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-[40%_60%]">

        {/* LEFT SIDE */}
        <div
          className="relative bg-cover bg-center px-6 py-12 sm:px-10 sm:py-16 lg:px-14"
          style={{
            backgroundImage:
              'url("/footer.webp")',
          }}
        >
          <div className="absolute inset-0 bg-black/60" />

          <div className="relative z-10 max-w-md mx-auto lg:mx-0">
            <div className="flex items-center gap-2 mb-5">
              <Leaf className="w-5 h-5" />
              <span className="text-xs sm:text-sm font-medium tracking-wider uppercase">
                {t('footer_updates_label')}
              </span>
            </div>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6">
              {t('footer_join_newsletter')}
            </h2>

            <form onSubmit={handleSubmit} className="mb-6">
              <div className="flex flex-col sm:flex-row bg-white/10 backdrop-blur-sm rounded-full overflow-hidden border border-white/20 focus-within:border-[#7EDE56] transition">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('footer_subscribe_placeholder')}
                  className="flex-1 bg-transparent px-4 py-3 text-white placeholder-white/60 outline-none text-sm"
                  required
                />
                <button
                  type="submit"
                  className="bg-black hover:bg-[#7EDE56] text-white hover:text-[#002F3F] font-semibold px-6 py-3 text-sm transition"
                >
                  {t('footer_subscribe_button')}
                </button>
              </div>
            </form>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="privacy"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 w-4 h-4"
              />
              <label
                htmlFor="privacy"
                className="text-xs sm:text-sm text-white/80 leading-relaxed"
              >
                {t('footer_unsubscribe_text')}{" "}
                {/* Privacy Policy and Terms pages do not exist yet. Kept as
                    plain text rather than links so the footer on all 17 pages
                    is not pointing at a 404. Restore the <Link>s to /privacy
                    and /terms once those pages are written. */}
                <span className="underline">{t('footer_privacy_policy')}</span>{" "}
                &{" "}
                <span className="underline">{t('footer_terms')}</span>
              </label>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="bg-[#002f37] px-6 py-12 sm:px-10 sm:py-16 lg:px-14">

          {/* LOGO + SOCIAL */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10 pb-6 border-b border-white/20">
            <div className="flex items-center gap-3">
              <Leaf className="w-7 h-7 text-[#7EDE56]" />
              <span className="text-2xl sm:text-3xl font-bold">
                {t('saleem_enterprises_brand')}
              </span>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-sm">{t('footer_follow_us')}</span>
              <div className="flex gap-4">
                <a
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Saleem Enterprises on LinkedIn"
                  className="hover:text-[#7EDE56] transition"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>

          {/* LINKS SECTION */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 mb-10">

            {/* CONTACT */}
            <div>
              <h3 className="text-lg font-semibold mb-5">{t('footer_contact_title')}</h3>
              <div className="space-y-4 text-sm text-white/80">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#7EDE56] mt-1" />
                  <p>
                    P-39, Scheme # 212, Part II, Dijkot Road, Faisalabad Pakistan
                  </p>
                </div>
               <a href="https://wa.me/923066665542" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 cursor-pointer" > <Phone className="w-5 h-5 text-[#7EDE56]" /> <span>(+92) 3066665542</span> </a>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-[#7EDE56]" />
                  <span>info@saleementerprises.com</span>
                </div>
              </div>
            </div>

            {/* QUICK LINKS */}
            <div>
              <h3 className="text-lg font-semibold mb-5">{t('footer_quick_links')}</h3>
              <ul className="space-y-3 text-sm text-white/80">
                <li>
                  <Link to="/" className="hover:text-[#7EDE56] cursor-pointer">
                    {t('home')}
                  </Link>
                </li>
                <li>
                  <Link to="/" className="hover:text-[#7EDE56] cursor-pointer">
                    {t('about_heading')}
                  </Link>
                </li>
                <li>
                  <Link to="/projects" className="hover:text-[#7EDE56] cursor-pointer">
                    {t('projects')}
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="hover:text-[#7EDE56] cursor-pointer">
                    {t('contact')}
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="hover:text-[#7EDE56] cursor-pointer">
                    {t('blog')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* SERVICES */}
            <div>
              <h3 className="text-lg font-semibold mb-5">{t('footer_services')}</h3>
              <ul className="space-y-3 text-sm text-white/80">
                <li>
                  <Link to="/automation-detail" className="hover:text-[#7EDE56] cursor-pointer">
                    {t('automation_control')}
                  </Link>
                </li>
                <li>
                  <Link to="/research-detail" className="hover:text-[#7EDE56] cursor-pointer">
                    {t('research_development')}
                  </Link>
                </li>
                <li>
                  <Link to="/quality-detail" className="hover:text-[#7EDE56] cursor-pointer">
                    {t('innovation_agriculture')}
                  </Link>
                </li>
                <li>
                  <Link to="/fertigation-systems" className="hover:text-[#7EDE56] cursor-pointer">
                    {t('climate_fertigation')}
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* BOTTOM */}
          <div className="pt-6 border-t border-white/20 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
            <p className="text-xs sm:text-sm text-white/60">
              {t('footer_license_text')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default NewFooter;